import React, { useState, useEffect, KeyboardEvent } from 'react';
import { useListFilterContext } from 'react-admin';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface SearchOnSubmitInputProps {
  source?: string;
  label?: string;
}

/**
 * Search input that only triggers filtering on Enter keypress or search button click.
 * Drop-in replacement for react-admin's FilterLiveSearch.
 */
const SearchOnSubmitInput: React.FC<SearchOnSubmitInputProps> = ({
  source = 'q',
  label = 'Search',
}) => {
  const { filterValues, setFilters } = useListFilterContext();
  const [value, setValue] = useState<string>((filterValues[source] as string) ?? '');

  // Sync local value when filters change externally (e.g., "Clear all filters" button)
  useEffect(() => {
    setValue((filterValues[source] as string) ?? '');
  }, [filterValues, source]);

  const submit = () => {
    setFilters({ ...filterValues, [source]: value || undefined }, {});
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      fullWidth
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={submit} aria-label="search">
              <SearchIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ mb: 1 }}
    />
  );
};

export default SearchOnSubmitInput;
